import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { cn } from "@/shared/lib";

import { useAssetLookup } from "../../api/useAssetLookup";
import { formatPickRate } from "../../format/formatPickRate";
import { formatSouls } from "../../format/formatSouls";
import type { CoreBuildItem } from "../../model/Analysis";
import { PURCHASE_VERDICT_LABELS } from "../../model/PURCHASE_VERDICT_LABELS";
import { PURCHASE_VERDICT_TONES } from "../../model/PURCHASE_VERDICT_TONES";
import type { Tone } from "../../model/Tone";
import { AssetIcon } from "../AssetIcon";

const ITEM = msg({ id: "analysis.core.item", message: "Item" });
const SOULS = msg({ id: "analysis.core.souls", message: "Souls when bought" });
const VERDICT = msg({ id: "analysis.core.verdict", message: "Verdict" });
const PICK = msg({ id: "analysis.core.pick", message: "{pick} take it" });
const VIA = msg({ id: "analysis.core.via", message: "bought inside {item}" });
const NOT_BOUGHT = msg({ id: "analysis.core.not_bought", message: "not bought" });
const BAR = msg({
  id: "analysis.core.souls_bar",
  message:
    "Usually bought at {from}–{to} souls, half by {median}. {mine, select, none {You did not buy it.} other {You bought it at {mine}.}}",
});
const LEGEND_BAND = msg({
  id: "analysis.core.legend.band",
  message: "8 of 10 buy it here",
});
const LEGEND_MEDIAN = msg({ id: "analysis.core.legend.median", message: "half buy it earlier" });
const LEGEND_YOU = msg({ id: "analysis.core.legend.you", message: "you" });

const CHIP_COLORS: Record<Tone, string> = {
  good: cn("border-good/40 bg-good/10 text-good"),
  warn: cn("border-warn/40 bg-warn/10 text-warn"),
  bad: cn("border-bad/40 bg-bad/10 text-bad"),
  neutral: cn("border-border bg-surface-raised text-muted"),
};
const DOT_COLORS: Record<Tone, string> = {
  good: "bg-good",
  warn: "bg-warn",
  bad: "bg-bad",
  neutral: "bg-neutral",
};

// Шкала — до ближайших 5 000 душ выше самой правой точки.
const SCALE_STEP = 5000;

type CoreBuildSoulsProps = {
  items: readonly CoreBuildItem[];
  caption: string;
};

const percent = (value: number, scale: number): string =>
  `${Math.min((value / scale) * 100, 100).toFixed(1)}%`;

// Подпись душ над точкой: у краёв полосы — прижата внутрь, чтобы не вылезать за неё.
const labelShift = (value: number, scale: number): string => {
  const share = value / scale;
  return share < 0.06 ? "translate-x-0" : share > 0.94 ? "-translate-x-full" : "-translate-x-1/2";
};

// Ключевые предметы полосами по душам к покупке, по доске `BuildFinal.dc.html`: полоса —
// у 8 из 10 сильных игроков, черта — у половины, точка — у игрока. Оценка — с бэкенда (как
// на таймлайне). На узком экране полоса уходит под название предмета.
export const CoreBuildSouls = ({ items, caption }: CoreBuildSoulsProps) => {
  const { _, i18n } = useLingui();
  const findItem = useAssetLookup("items");
  if (items.length === 0) {
    return null;
  }
  const souls = (value: number) => formatSouls(value, i18n.locale);
  const right = Math.max(...items.map((item) => Math.max(item.souls_p90 ?? 0, item.souls ?? 0)));
  const scale = Math.max(Math.ceil((right * 1.05) / SCALE_STEP) * SCALE_STEP, SCALE_STEP);
  return (
    <div className="@container flex flex-col gap-2">
      <p className="text-[13px] text-subtle">{caption}</p>
      <div role="table" aria-label={caption} className="flex flex-col">
        <div
          role="row"
          className="hidden border-b border-border pb-2 text-xs text-subtle @lg:grid @lg:grid-cols-[220px_minmax(0,1fr)_130px] @lg:gap-3"
        >
          <span role="columnheader">{_(ITEM)}</span>
          <span role="columnheader">{_(SOULS)}</span>
          <span role="columnheader" className="text-right">
            {_(VERDICT)}
          </span>
        </div>
        {items.map((item) => {
          const asset = findItem(item.item_id);
          const tone: Tone =
            item.verdict === null ? "neutral" : PURCHASE_VERDICT_TONES[item.verdict];
          const p10 = item.souls_p10 ?? 0;
          const p50 = item.souls_p50 ?? 0;
          const p90 = item.souls_p90 ?? 0;
          return (
            <div
              key={item.item_id}
              role="row"
              data-core-item={item.item_id}
              data-verdict={item.verdict ?? "not_bought"}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 border-b border-border py-2 last:border-b-0 @lg:grid-cols-[220px_minmax(0,1fr)_130px]"
            >
              <span role="cell" className="flex min-w-0 items-center gap-2.5">
                <AssetIcon
                  asset={asset}
                  size={36}
                  className={cn("rounded-md", item.bought_at_s === null && "opacity-55")}
                />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-medium text-foreground">{asset.name}</span>
                  <span className="text-xs text-subtle">
                    {item.via_item_id !== null
                      ? _({ ...VIA, values: { item: findItem(item.via_item_id).name } })
                      : _({
                          ...PICK,
                          values: { pick: formatPickRate(item.pick_rate, i18n.locale) },
                        })}
                  </span>
                </span>
              </span>
              <span
                role="cell"
                className="relative order-last col-span-2 block h-[34px] @lg:order-none @lg:col-span-1"
              >
                <span
                  role="img"
                  aria-label={_({
                    ...BAR,
                    values: {
                      from: souls(p10),
                      to: souls(p90),
                      median: souls(p50),
                      mine: item.souls === null ? "none" : souls(item.souls),
                    },
                  })}
                  className="absolute inset-0"
                >
                  <span className="absolute top-4 right-0 left-0 h-0.5 bg-border" />
                  <span
                    className="absolute top-[11px] h-3 rounded-md border border-accent/55 bg-accent/25"
                    style={{
                      left: percent(p10, scale),
                      width: `max(6px, calc(${percent(p90, scale)} - ${percent(p10, scale)}))`,
                    }}
                  />
                  <span
                    className="absolute top-2 h-[18px] w-0.5 -translate-x-1/2 rounded-sm bg-accent"
                    style={{ left: percent(p50, scale) }}
                  />
                  {item.souls !== null && (
                    <>
                      <span
                        className={cn(
                          "absolute top-2.5 size-3.5 -translate-x-1/2 rounded-full border-2 border-surface",
                          DOT_COLORS[tone],
                        )}
                        style={{ left: percent(item.souls, scale) }}
                      />
                      <span
                        aria-hidden="true"
                        className={cn(
                          "absolute -top-1.5 font-mono text-[11px] whitespace-nowrap text-foreground",
                          labelShift(item.souls, scale),
                        )}
                        style={{ left: percent(item.souls, scale) }}
                      >
                        {souls(item.souls)}
                      </span>
                    </>
                  )}
                </span>
              </span>
              <span role="cell" className="text-right">
                <span
                  className={cn(
                    "inline-flex rounded-full border px-2.5 py-0.5 text-xs whitespace-nowrap",
                    CHIP_COLORS[tone],
                  )}
                >
                  {item.verdict === null ? _(NOT_BOUGHT) : _(PURCHASE_VERDICT_LABELS[item.verdict])}
                </span>
              </span>
            </div>
          );
        })}
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-subtle">
        <li className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="h-2.5 w-5 rounded-[5px] border border-accent/55 bg-accent/25"
          />
          {_(LEGEND_BAND)}
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="h-3.5 w-0.5 bg-accent" />
          {_(LEGEND_MEDIAN)}
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="size-3 rounded-full bg-good" />
          {_(LEGEND_YOU)}
        </li>
      </ul>
    </div>
  );
};
